<script setup>
import { ref, computed, onMounted } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { formatShortDateTime } from '@/lib/utils'
import { useDetectDevice } from '@/composables/device'
import { useAccountingStore } from '@/stores/accounting.store'
import ReportFormattedGrid from '@/components/accounting/ReportFormattedGrid.vue'
import ReportMobileSections from '@/components/accounting/ReportMobileSections.vue'
import ReportMobileSummary from '@/components/accounting/ReportMobileSummary.vue'
import ReportPendingChanges from '@/components/accounting/ReportPendingChanges.vue'
import ReportPendingNotice from '@/components/accounting/ReportPendingNotice.vue'

const props = defineProps({
  report: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['recompute'])

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const accountingStore = useAccountingStore()

const rendered = ref(null)
const sourceRows = ref([])
const pendingChanges = ref({ rows: [], deleted_count: 0 })
const showPendingChanges = ref(false)

const formatMoment = (value) => (value ? formatShortDateTime(value) : '—')

const pendingSummary = computed(() => {
  const { rows, deleted_count: deleted } = pendingChanges.value
  const added = rows.filter(change => change.is_new).length
  const changed = rows.length - added
  return [
    added ? i18n.t('common.report.pendingAddedCount', added) : '',
    changed ? i18n.t('common.report.pendingChangedCount', changed) : '',
    deleted ? i18n.t('common.report.pendingDeleted', { count: deleted }) : '',
  ].filter(Boolean).join(' · ')
})

const hasPendingChanges = computed(() => pendingChanges.value.rows.length > 0 || pendingChanges.value.deleted_count > 0)

const onRecompute = () => {
  showPendingChanges.value = false
  emit('recompute')
}

onMounted(async() => {
  const [rows, result] = await Promise.all([
    accountingStore.fetchReportRowsAsync(props.report.id),
    accountingStore.renderReportAsync({ report: props.report.id }),
  ])
  sourceRows.value = rows.rows
  pendingChanges.value = rows.pending_changes
  rendered.value = result
})
</script>

<template>
  <div :class="isMobile ? 'full-height' : 'q-pa-sm'">
    <q-dialog
      :model-value="showPendingChanges"
      class="dialog-xs"
      :maximized="isMobile"
      @hide="showPendingChanges = false"
    >
      <report-pending-changes
        v-if="showPendingChanges"
        :changes="pendingChanges"
        :data-as-of="props.report.rows_computed_at"
        @recompute="onRecompute"
        @cancel="showPendingChanges = false"
      />
    </q-dialog>
    <q-inner-loading :showing="rendered === null" />
    <report-mobile-sections
      v-if="isMobile && rendered"
      :build="rendered.build"
      :formatted="rendered.formatted"
      :subject="props.report.subject"
      :source-rows="sourceRows"
    >
      <report-mobile-summary :build="rendered.build" :report="props.report" />
      <div v-if="hasPendingChanges" class="q-px-sm q-pb-sm">
        <report-pending-notice :summary="pendingSummary" @details="showPendingChanges = true" @recompute="emit('recompute')" />
      </div>
      <q-separator />
    </report-mobile-sections>
    <template v-else-if="rendered">
      <div class="row q-gutter-x-md text-caption text-grey-7 q-mb-sm">
        <span>{{ i18n.t('common.report.createdOn') }} <b>{{ formatMoment(props.report.created_at) }}</b></span>
        <span>{{ i18n.t('common.report.updatedOn') }} <b>{{ formatMoment(props.report.updated_at) }}</b></span>
        <span>{{ i18n.t('common.report.dataAsOf') }} <b>{{ formatMoment(props.report.rows_computed_at) }}</b></span>
      </div>
      <report-pending-notice
        v-if="hasPendingChanges"
        class="q-mb-sm"
        :summary="pendingSummary"
        inline
        @details="showPendingChanges = true"
        @recompute="emit('recompute')"
      />
      <report-formatted-grid :formatted="rendered.formatted" />
    </template>
  </div>
</template>
